import {
  DeleteIcon,
  LogoutIcon,
  PaletteIcon,
  SettingsIcon,
  UserCircleIcon,
  WalletIcon,
} from './icons';
import React from 'react';
import { Divider } from '@mui/material';
import { NavigationMenuList, NavigationMenuRow } from './NavigationMenuRow';
import { MenuPresentation } from './MenuPresentation';
import { DialogHeader } from './DialogHeader';

export type MoreDestination =
  | 'shared-with-me'
  | 'bin'
  | 'collection'
  | 'billing'
  | 'appearance'
  | 'account';

export function MoreSheet({
  open,
  onClose,
  onSelect,
  onSignOut,
  showBilling = false,
  collectionName,
  closeLabel = 'Close menu',
  embedded = false,
}: {
  embedded?: boolean;
  open: boolean;
  onClose: () => void;
  onSelect: (destination: MoreDestination) => void;
  onSignOut?: () => void;
  showBilling?: boolean;
  collectionName?: string;
  closeLabel?: string;
}) {
  const entries: Array<{
    key: MoreDestination;
    title: string;
    subtitle?: string;
    icon: React.ReactNode;
  }> = [
    ...(collectionName
      ? [
          {
            key: 'collection' as const,
            title: 'Collection settings',
            subtitle: collectionName,
            icon: <SettingsIcon />,
          },
        ]
      : []),
    {
      key: 'shared-with-me',
      title: 'Shared with me',
      icon: <UserCircleIcon />,
    },
    { key: 'appearance', title: 'Appearance', icon: <PaletteIcon /> },
    { key: 'account', title: 'Account', icon: <UserCircleIcon /> },
    ...(showBilling
      ? [{ key: 'billing' as const, title: 'Billing', icon: <WalletIcon /> }]
      : []),
    { key: 'bin', title: 'Bin', icon: <DeleteIcon /> },
  ];
  const content = (
    <>
      <DialogHeader
        id="more-sheet-title"
        title="Menu"
        onClose={onClose}
        closeLabel={closeLabel}
      />
      <NavigationMenuList label="Secondary actions">
        {entries.map(({ key, title, subtitle, icon }) => (
          <React.Fragment key={key}>
            <NavigationMenuRow
              icon={icon}
              label={title}
              description={subtitle}
              onClick={() => onSelect(key)}
            />
            {key === 'collection' && <Divider component="li" />}
          </React.Fragment>
        ))}
        {onSignOut && (
          <NavigationMenuRow
            icon={<LogoutIcon />}
            label="Sign out"
            onClick={onSignOut}
            tone="danger"
            showChevron={false}
          />
        )}
      </NavigationMenuList>
    </>
  );
  if (embedded) return open ? content : null;
  return (
    <MenuPresentation open={open} onClose={onClose} titleId="more-sheet-title">
      {content}
    </MenuPresentation>
  );
}
